import { RolexContainer } from './Container'
import { Image } from '@components/gassan-ui'
import { Button } from '@components/rolex-ui/Button'
import { Heading } from '@components/rolex-ui/Heading'
import { Text } from '@components/rolex-ui/Text'
import { cn } from '@lib/cn'
import { MultipleItemResponse, getKontentImage } from '@lib/kontent'
import { fetchRolexV7Products } from '@lib/kontent/fetch-rolex-v7-products'
import { RolexProductPageV72024 } from '@lib/kontent/models/RolexProductPageV72024'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { FC } from 'react'

type RolexCollectionModelOverviewProps = {
  title: string
  language: string
  fid: string
  kontentFid: string
  initialData: MultipleItemResponse<RolexProductPageV72024>
}

export const RolexCollectionModelOverview: FC<RolexCollectionModelOverviewProps> = ({
  title,
  fid,
  kontentFid,
  language,
  initialData,
}) => {
  const { t } = useTranslation('rolex')
  const totalCount = initialData.pagination.total_count
  const { fetchNextPage, data, hasNextPage, isFetching } = useInfiniteQuery({
    queryKey: ['rolex-family-models', { fid, language }],
    queryFn: async ({ pageParam }) =>
      fetchRolexV7Products({
        fid: kontentFid,
        language,
        skip: pageParam,
      }),
    getNextPageParam: (lastPage, pages) => {
      const fetchedItems = pages.flatMap((page) => page.items)
      if (fetchedItems.length >= totalCount) return undefined

      return lastPage.pagination.skip + lastPage.pagination.limit
    },
    initialData: {
      pages: [initialData],
      pageParams: [0],
    },
  })

  return (
    <section className="bg-rolexLightBeige py-[30px] md:py-[90px]">
      <RolexContainer as="div" className="!max-w-[1044px]">
        <div className="flex w-full justify-center">
          <Heading
            variant="headline50"
            className="mb-[30px] text-center text-rolexBrown md:mb-[50px]"
          >
            {title} models
          </Heading>
        </div>
        <div className="mb-[30px] grid grid-cols-2 gap-2 md:mb-10 md:grid-cols-3">
          {data &&
            data.pages.map((page) =>
              page.items.map((item) => (
                <Link
                  key={item.system.id}
                  href={`/rolex/${fid.replaceAll('_', '-')}/${item.elements.url_slug.value}`}
                  passHref
                >
                  <a className="group flex flex-col items-start bg-rolexBeige px-5 pb-[30px] md:px-[30px] md:pb-[50px] md:pt-5 lg:px-[50px]">
                    <Image
                      className={cn(
                        'aspect-[2/3] justify-self-center opacity-100 transition-opacity duration-150 group-hover:opacity-80',
                      )}
                      sources={[
                        getKontentImage(item.elements.main_image.value[0]?.url, { width: 768 }),
                      ]}
                      placeholderSrc={getKontentImage(item.elements.main_image.value[0]?.url, {
                        width: 50,
                        height: 50,
                      })}
                      alt={item.elements.main_image.value[0]?.description as string}
                    />
                    <div>
                      <Text variant="legend16" className="text-rolexBrown">
                        Rolex
                      </Text>
                      <Text variant="body24" className="mb-1 text-rolexBrown">
                        {item.elements.title.value}
                      </Text>
                      <Text variant="legend16" weight="light">
                        {item.elements.short_description.value}
                      </Text>
                    </div>
                  </a>
                </Link>
              )),
            )}
        </div>
        {hasNextPage && (
          <div className="flex w-full justify-center">
            <Button
              as="button"
              status={isFetching ? 'disabled' : 'idle'}
              variant="primary"
              onClick={() => fetchNextPage()}
            >
              {t('loadMore')}
            </Button>
          </div>
        )}
      </RolexContainer>
    </section>
  )
}
